import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useParams } from "react-router-dom";

import Api from "../../../shared/api/api";
import { getStoredOrLatestTestId } from "../helpers/fourHTest";

import MessageModal from "../components/MessageModal";
import Question from "../components/Question";
import WarningMsg from "../components/WarningMsg";

import "../styles/Test.css";

const normalizeAnswer = (value) =>
  String(value || "").trim().toLowerCase();

export default function TestPage() {
  const { userId } = useParams();

  const [userResponses, setUserResponses] =
    useState({});

  const [questions, setQuestions] = useState([]);
  const [user, setUser] = useState(null);
  const [testId, setTestId] = useState(null);

  const [isLoading, setIsLoading] =
    useState(true);

  const [isSubmitting, setIsSubmitting] =
    useState(false);

  const [testComplete, setTestComplete] =
    useState(false);

  const [error, setError] = useState("");

  const submissionStartedRef = useRef(false);
  const testCompleteRef = useRef(false);
  const responsesRef = useRef({});

  useEffect(() => {
    responsesRef.current = userResponses;
  }, [userResponses]);

  useEffect(() => {
    testCompleteRef.current = testComplete;
  }, [testComplete]);

  useEffect(() => {
    let isMounted = true;

    const loadTest = async () => {
      setIsLoading(true);
      setError("");

      try {
        const latestTestId =
          await getStoredOrLatestTestId();

        if (!latestTestId) {
          throw new Error(
            "No active test was found."
          );
        }

        const [userResponse, questionResponse] =
          await Promise.all([
            Api.getUserById(userId),
            Api.getTestQuestions(
              latestTestId
            ),
          ]);

        if (
          userResponse?.error ||
          !userResponse?.four_h_id
        ) {
          throw new Error(
            userResponse?.error ||
              "The participant could not be found."
          );
        }

        if (questionResponse?.error) {
          throw new Error(
            questionResponse.error
          );
        }

        if (isMounted) {
          setTestId(latestTestId);
          setUser(userResponse);

          setQuestions(
            Array.isArray(questionResponse)
              ? questionResponse
              : []
          );
        }
      } catch (requestError) {
        console.error(
          "Unable to load FourH test:",
          requestError
        );

        if (isMounted) {
          setError(
            requestError?.message ||
              "The test could not be loaded."
          );
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadTest();

    return () => {
      isMounted = false;
    };
  }, [userId]);

  const filteredQuestions = useMemo(() => {
    if (!user?.level) {
      return [];
    }

    return questions.filter(
      (question) =>
        String(question.level) ===
        String(user.level)
    );
  }, [questions, user]);

  const knowledgeQuestions = useMemo(
    () =>
      filteredQuestions.filter(
        (question) =>
          question.question_type ===
          "Knowledge"
      ),
    [filteredQuestions]
  );

  const identificationQuestions = useMemo(
    () =>
      filteredQuestions.filter(
        (question) =>
          question.question_type ===
          "Identification"
      ),
    [filteredQuestions]
  );

  const updateUserResponse = (
    questionId,
    answer
  ) => {
    setUserResponses((currentResponses) => ({
      ...currentResponses,
      [questionId]: answer,
    }));
  };

  const submitAssessment = useCallback(
    async ({ automatic = false } = {}) => {
      if (
        submissionStartedRef.current ||
        testCompleteRef.current ||
        filteredQuestions.length === 0 ||
        !user ||
        !testId
      ) {
        return;
      }

      submissionStartedRef.current = true;
      setIsSubmitting(true);
      setError("");

      try {
        let knowledgeScore = 0;
        let identificationScore = 0;

        const answers = filteredQuestions.map(
          (question) => {
            const submittedAnswer =
              responsesRef.current[
                question.id
              ] || "";

            const isCorrect =
              normalizeAnswer(submittedAnswer) ===
              normalizeAnswer(question.answer);

            if (
              isCorrect &&
              question.question_type ===
                "Knowledge"
            ) {
              knowledgeScore += 1;
            }

            if (
              isCorrect &&
              question.question_type ===
                "Identification"
            ) {
              identificationScore += 1;
            }

            return {
              questionId: question.id,
              testId:
                question.test_id ?? testId,
              userId: user.four_h_id,
              uAnswer: [
                submittedAnswer,
                {
                  corrected: false,
                },
              ],
              cAnswer: normalizeAnswer(
                question.answer
              ),
              points: isCorrect ? 1 : 0,
              questionType:
                question.question_type,
            };
          }
        );

        const response =
          await Api.addUserAssessment(
            user.four_h_id,
            testId,
            JSON.stringify(answers),
            true,
            true,
            null,
            false,
            false,
            knowledgeScore,
            identificationScore
          );

        if (response?.error) {
          throw new Error(response.error);
        }

        setTestComplete(true);
        testCompleteRef.current = true;
      } catch (requestError) {
        console.error(
          "Unable to submit FourH assessment:",
          requestError
        );

        submissionStartedRef.current = false;

        setError(
          automatic
            ? "The test was automatically submitted, but the submission could not be confirmed. Please contact your moderator."
            : requestError?.message ||
                "The test could not be submitted."
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      filteredQuestions,
      testId,
      user,
    ]
  );

  useEffect(() => {
    if (
      isLoading ||
      testComplete ||
      filteredQuestions.length === 0
    ) {
      return undefined;
    }

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";

      submitAssessment({
        automatic: true,
      });
    };

    const handleVisibilityChange = () => {
      if (
        document.visibilityState === "hidden"
      ) {
        submitAssessment({
          automatic: true,
        });
      }
    };

    const handlePopState = () => {
      submitAssessment({
        automatic: true,
      });
    };

    window.addEventListener(
      "beforeunload",
      handleBeforeUnload
    );

    window.addEventListener(
      "popstate",
      handlePopState
    );

    document.addEventListener(
      "visibilitychange",
      handleVisibilityChange
    );

    return () => {
      window.removeEventListener(
        "beforeunload",
        handleBeforeUnload
      );

      window.removeEventListener(
        "popstate",
        handlePopState
      );

      document.removeEventListener(
        "visibilitychange",
        handleVisibilityChange
      );
    };
  }, [
    filteredQuestions.length,
    isLoading,
    submitAssessment,
    testComplete,
  ]);

  if (isLoading) {
    return (
      <main className="fourHHomepage">
        <div className="testContainer">
          <p role="status">
            Loading questions...
          </p>
        </div>
      </main>
    );
  }

  if (error && !user) {
    return (
      <main className="fourHHomepage">
        <div className="testContainer">
          <WarningMsg
            message={error}
            type="error"
          />
        </div>
      </main>
    );
  }

  return (
    <main className="fourHHomepage">
      <div className="testContainer">
        {testComplete ? (
          <MessageModal
            header="Thank You!"
            message="Congratulations, you have completed the test. Your scores will be submitted to your leader."
            url="/fourh"
            buttonText="Return Home"
          />
        ) : (
          <>
            {error && (
              <WarningMsg
                message={error}
                type="error"
              />
            )}

            {knowledgeQuestions.length > 0 && (
              <section className="testQuestionSection">
                <h1 className="questionTypeH1">
                  Multiple Choice Questions
                </h1>

                {knowledgeQuestions.map(
                  (question) => (
                    <Question
                      key={question.id}
                      q={question}
                      u={user}
                      updateResponse={
                        updateUserResponse
                      }
                      initialValue={
                        userResponses[
                          question.id
                        ] || ""
                      }
                    />
                  )
                )}
              </section>
            )}

            {identificationQuestions.length >
              0 && (
              <section className="testQuestionSection">
                <h1 className="questionTypeH1">
                  Identify the Following
                </h1>

                {identificationQuestions.map(
                  (question) => (
                    <Question
                      key={question.id}
                      q={question}
                      u={user}
                      updateResponse={
                        updateUserResponse
                      }
                      initialValue={
                        userResponses[
                          question.id
                        ] || ""
                      }
                    />
                  )
                )}
              </section>
            )}

            {filteredQuestions.length === 0 && (
              <WarningMsg
                message="No questions were found for this participant's level."
                type="error"
              />
            )}

            {filteredQuestions.length > 0 && (
              <div className="submitTestBtnDiv">
                <button
                  className="submitTestBtn"
                  type="button"
                  onClick={() =>
                    submitAssessment({
                      automatic: false,
                    })
                  }
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? "Submitting..."
                    : "Submit Test"}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </main>
  );
}